import sanitize from 'sanitize-html'

import { PathnamePrefix } from './language'

import { CategoryId } from '@/features/community/api/document/community-document.types'

export function removeDangerousFields<T>(
  obj: T & Partial<{ originIp: string; writerName: string }>,
): Omit<T, 'originIp' | 'writerName'> {
  const clonedObj = { ...obj }

  delete clonedObj.originIp
  delete clonedObj.writerName

  return clonedObj
}

export function makeHashtagsAsButtons(text: string, urlPrefix: PathnamePrefix): string {
  const result = sanitize(text, {
    allowedTags: ['span'],
    allowedClasses: {
      span: ['show-more-text', 'link-style'],
    },
  }).replace(
    /[#＃][^\s#＃]+/g,
    (match: string) =>
      `<a href="${urlPrefix}/search?q=${match.replace(
        /[#＃]/,
        '',
      )}" class="hash-tag-button" data-testid="content-hashtag">${match}</a>`,
  )

  return result
}

export function refineContents(
  item: {
    contents: string
    categoryId?: CategoryId
    translateResultContents?: string
  },
  showMoreText: string,
  pathPrefix: PathnamePrefix,
): string {
  const newLinesRegexPattern = `\\n{${2},}`
  const newLinesRegex = new RegExp(newLinesRegexPattern, 'g')

  const targetContents = item.contents

  if (!targetContents) {
    return ''
  }

  const newLinesRemovedContents = targetContents.replace(newLinesRegex, '\n\n')

  let refinedContents: string = newLinesRemovedContents

  const CONTENTS_LENGTH_LIMIT = 60

  if (newLinesRemovedContents.length > CONTENTS_LENGTH_LIMIT) {
    const slicedMarkdownConvertedContents = newLinesRemovedContents.slice(0, CONTENTS_LENGTH_LIMIT)

    const sanitizedContents = sanitize(slicedMarkdownConvertedContents)

    refinedContents = sanitizedContents + ` <span class="show-more-text">...${showMoreText}</span>`
  }

  return makeHashtagsAsButtons(refinedContents, pathPrefix)
}

export function extractTextFromHTML(html: string): string {
  const regex = /<[^>]+>([^<]*)<\/[^>]+>/g
  const matches = html.match(regex)

  if (!matches) {
    return ''
  }

  return matches.map((match) => match.replace(/<[^>]+>/g, '')).join(' ')
}

export function refineEmojiAtSlicingPart(text: string, length: number): string {
  const emojiRegex = /\p{Emoji}/gu

  const matches = text.slice(0, length + 1).match(emojiRegex)
  const indexOfFirstEmoji = text.slice(0, length + 1).indexOf(matches?.[0] || '')
  const lengthOfFirstEmoji = matches?.[0].length || 0

  const isEmojiAtSlicingPart = indexOfFirstEmoji + lengthOfFirstEmoji > length

  if (isEmojiAtSlicingPart) {
    return text.slice(0, indexOfFirstEmoji + lengthOfFirstEmoji)
  }

  return text.slice(0, length)
}

export type CommunityContentVisibility = 'VISIBLE' | 'HIDDEN_DOCTOR_CONTENT' | 'HIDDEN_REVIEW_CONTENT'

export function getCommunityContentVisibility(
  userId: number | undefined,
  writerDoctorId: number | null,
  categoryId: CategoryId,
): CommunityContentVisibility {
  if (userId && userId !== 0) {
    return 'VISIBLE'
  }

  if (writerDoctorId !== null) {
    return 'HIDDEN_DOCTOR_CONTENT'
  }

  if (categoryId === CategoryId.REVIEWS) {
    return 'HIDDEN_REVIEW_CONTENT'
  }

  return 'VISIBLE'
}
